import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';

import { BlockTextComponent } from '../../common/block-text/block-text.component';
import { FieldEditorV2Component } from '../../base-template-editor-v2/field-editor-v2/field-editor-v2.component';
import { InlineEditV2Directive } from '../../base-template-editor-v2/inline-edit/inline-edit-v2.directive';
import { InlineMediaV2Directive } from '../../base-template-editor-v2/inline-edit/inline-media-v2.directive';
import { BaseComponent } from '../../common/BaseComponent';
import { Helper } from '../../../../utils/helper';
import {
  PropertyUsp2xPhotoInitModel,
  PropertyUsp2xPhotoInitVariables,
  PropertyUsp2xPhotoModel,
  PropertyUsp2xPhotoVariables,
} from '../../../../models/email-template/property-usp-2x-photo.interface';

/**
 * Mockup-styled Property + USP + 2x Photo block - a drop-in replacement for
 * app-propertyusp2xphoto in block-renderer-v2 (same [block]/[isPreview]
 * inputs and outputs), so it covers both the center-canvas summary
 * (isPreview=true) and the right-panel edit form (isPreview=false). Reuses
 * the real, unmodified app-block-text (image upload/segment/validation) and
 * app-field-editor-v2/app-pin sub-components and the exact same
 * PropertyUsp2xPhotoVariables field names - only the surrounding layout is
 * new. Two independent photos of the same property (not desktop/mobile
 * variants - each has its own href/alt), one shared name + USP list. No CTA
 * Link/CTA Text edit fields, matching v1 exactly (see property-usp-1x-photo-v2).
 */
@Component({
  selector: 'app-property-usp-2x-photo-v2',
  imports: [CommonModule, ReactiveFormsModule, BlockTextComponent, FieldEditorV2Component, InlineEditV2Directive, InlineMediaV2Directive],
  templateUrl: './property-usp-2x-photo-v2.component.html',
  styleUrl: './property-usp-2x-photo-v2.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class PropertyUsp2xPhotoComponentV2 extends BaseComponent implements OnInit {
  @Input() block: PropertyUsp2xPhotoModel = new PropertyUsp2xPhotoInitModel();
  @Input() isPreview = false;
  @Input() disabled = false;

  @Output() blockData = new EventEmitter<any>();
  @Output() removeBlockEvent = new EventEmitter<string | null>();

  ngOnInit() {
    if (Helper.isEmpty(this.block.variables!)) {
      this.block.variables = new PropertyUsp2xPhotoInitVariables();
    }
  }

  image1Placeholder = 'First Image';
  image2Placeholder = 'Second Image';
  image1HrefPlaceholder = 'Enter First Image Redirect Link';
  image2HrefPlaceholder = 'Enter Second Image Redirect Link';
  image1AltPlaceholder = 'Enter First Image Alt Text';
  image2AltPlaceholder = 'Enter Second Image Alt Text';
  namePlaceholder = 'Enter Name';
  usp1Placeholder = 'Enter USP 1';
  usp2Placeholder = 'Enter USP 2';
  usp3Placeholder = 'Enter USP 3';

  /** Same checkmark asset v1 renders in its own preview (production URL). */
  protected readonly checkmarkIconUrl =
    'https://email.valamar.com/assets/responsysimages/content/valamarri/afasfafsaf244.png';

  protected image1AsUpload = true;
  protected image2AsUpload = true;

  protected toggleImage1Mode(): void {
    this.image1AsUpload = !this.image1AsUpload;
  }

  protected toggleImage2Mode(): void {
    this.image2AsUpload = !this.image2AsUpload;
  }

  private hrefFor(link?: string, segment?: string): string {
    const base = this.helper.getFullUrl(link);
    if (!segment) {
      return base;
    }
    const separator = base.includes('?') ? '&' : '?';
    return `${base}${separator}segment=${encodeURIComponent(segment)}`;
  }

  get image1Href(): string {
    return this.hrefFor(this.block.variables?.['PropertyUsp2x1ImageHref'], this.block.variables?.['PropertyUsp2x1ImageHrefSegmentRegex']);
  }

  get image2Href(): string {
    return this.hrefFor(this.block.variables?.['PropertyUsp2x2ImageHref'], this.block.variables?.['PropertyUsp2x2ImageHrefSegmentRegex']);
  }

  removeBlock(): void {
    this.removeBlockEvent.emit(this.block.blockId);
  }

  onChange(value: any, fieldName: keyof PropertyUsp2xPhotoVariables) {
    if (this.block.variables) {
      (this.block.variables as any)[fieldName] = value;
      this.blockData.emit(this.block);
    }
  }


  handleOnSegmentChange(segment: string, blockName: string) {
    if (this.block.variables) {
      (this.block.variables as any)[blockName] = segment;
      this.blockData.emit(this.block);
    }
  }
}
